import React, { useState } from 'react';
import Layout from '../../components/ui/Layout';
import SubHeader from '../../components/ui/SubHeader';
import ActionBoxes from '../../components/ui/ActionBoxes';
import NotificationBar from './components/NotificationBar';
import ChannelSidebar from './components/ChannelSidebar';
import ConversationPanel from './components/ConversationPanel';
import ContactDetailsPanel from './components/ContactDetailsPanel';
import NewMessageModal from './components/NewMessageModal';
import BulkSendModal from './components/BulkSendModal';
import ScheduleFollowUpModal from './components/ScheduleFollowUpModal';
import TemplatesModal from './components/TemplatesModal';
import CommunicationReportModal from './components/CommunicationReportModal';
import WhatsAppActionsModal from './components/WhatsAppActionsModal';
import SMSActionsModal from './components/SMSActionsModal';
import EmailActionsModal from './components/EmailActionsModal';


const CommunicationAndFollowUpCenter = () => {
  const [activeChannel, setActiveChannel] = useState('whatsapp');
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [isQuickMenuOpen, setIsQuickMenuOpen] = useState(false);
  
  // Modal states
  const [showNewMessageModal, setShowNewMessageModal] = useState(false);
  const [showBulkSendModal, setShowBulkSendModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  
  // New action modals
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [showSMSModal, setShowSMSModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);

  // Client context for AI chatbot
  const clientContext = selectedConversation ? {
    name: selectedConversation?.clientName,
    type: 'potential_buyer',
    interests: [selectedConversation?.propertyInterest]
  } : {};

  // Communication action boxes
  const communicationActions = [
    {
      id: 'whatsapp',
      icon: 'MessageSquare',
      label: 'واتساب',
      color: '#25D366',
      bgGradient: 'linear-gradient(135deg, rgba(37, 211, 102, 0.2) 0%, rgba(37, 211, 102, 0.25) 100%)',
      borderColor: 'rgba(37, 211, 102, 0.5)',
      onClick: () => setShowWhatsAppModal(true)
    },
    {
      id: 'sms',
      icon: 'MessageCircle',
      label: 'رسائل SMS',
      color: '#3B82F6',
      bgGradient: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(96, 165, 250, 0.25) 100%)',
      borderColor: 'rgba(59, 130, 246, 0.5)',
      onClick: () => setShowSMSModal(true)
    },
    {
      id: 'email',
      icon: 'Mail',
      label: 'البريد الإلكتروني',
      color: '#F59E0B',
      bgGradient: 'linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(251, 191, 36, 0.25) 100%)',
      borderColor: 'rgba(245, 158, 11, 0.5)',
      onClick: () => setShowEmailModal(true)
    },
    {
      id: 'new-message',
      icon: 'Plus',
      label: 'رسالة جديدة',
      color: '#10B981',
      bgGradient: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(52, 211, 153, 0.25) 100%)',
      borderColor: 'rgba(16, 185, 129, 0.5)',
      onClick: () => setShowNewMessageModal(true)
    },
    {
      id: 'bulk-send',
      icon: 'Send',
      label: 'إرسال جماعي',
      color: '#EF4444',
      bgGradient: 'linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(248, 113, 113, 0.25) 100%)',
      borderColor: 'rgba(239, 68, 68, 0.5)',
      onClick: () => setShowBulkSendModal(true)
    },
    {
      id: 'schedule',
      icon: 'Calendar',
      label: 'جدولة متابعة',
      color: '#8B5CF6',
      bgGradient: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(167, 139, 250, 0.25) 100%)',
      borderColor: 'rgba(139, 92, 246, 0.5)',
      onClick: () => setShowScheduleModal(true)
    },
    {
      id: 'templates',
      icon: 'FileText',
      label: 'القوالب',
      color: '#06B6D4',
      bgGradient: 'linear-gradient(135deg, rgba(6, 182, 212, 0.2) 0%, rgba(34, 211, 238, 0.25) 100%)',
      borderColor: 'rgba(6, 182, 212, 0.5)',
      onClick: () => setShowTemplatesModal(true)
    },
    {
      id: 'report',
      icon: 'BarChart3',
      label: 'تقرير التواصل',
      color: '#EC4899',
      bgGradient: 'linear-gradient(135deg, rgba(236, 72, 153, 0.2) 0%, rgba(244, 114, 182, 0.25) 100%)',
      borderColor: 'rgba(236, 72, 153, 0.5)',
      onClick: () => setShowReportModal(true)
    }
  ];

  const breadcrumbItems = [
    { label: 'الرئيسية', path: '/property-management-dashboard', icon: 'Home' },
    { label: 'مركز التواصل والمتابعة', path: '/communication-and-follow-up-center', icon: 'MessageSquare' }
  ];

  const quickActions = [
    {
      label: 'رسالة جديدة',
      icon: 'Plus',
      onClick: () => setShowNewMessageModal(true),
      roles: ['admin', 'client_manager'],
      variant: 'primary'
    },
    {
      label: 'إرسال جماعي',
      icon: 'Send',
      onClick: () => setShowBulkSendModal(true),
      roles: ['admin', 'client_manager'],
      variant: 'default'
    },
    {
      label: 'جدولة متابعة',
      icon: 'Calendar',
      onClick: () => setShowScheduleModal(true),
      roles: ['admin', 'client_manager'],
      variant: 'default'
    },
    {
      label: 'القوالب',
      icon: 'FileText',
      onClick: () => setShowTemplatesModal(true),
      roles: ['admin', 'client_manager'],
      variant: 'default'
    },
    {
      label: 'تقرير التواصل',
      icon: 'BarChart3',
      onClick: () => setShowReportModal(true),
      roles: ['admin'],
      variant: 'default'
    }
  ];

  const actionBoxesConfig = [
    {
      id: 'new-message',
      icon: 'MessageSquarePlus',
      label: 'رسالة جديدة',
      color: '#3B82F6',
      bgGradient: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(96, 165, 250, 0.25) 100%)',
      borderColor: 'rgba(59, 130, 246, 0.5)',
      onClick: () => console.log('New Message')
    },
    {
      id: 'follow-up',
      icon: 'Clock',
      label: 'متابعة',
      color: '#F59E0B',
      bgGradient: 'linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(251, 191, 36, 0.25) 100%)',
      borderColor: 'rgba(245, 158, 11, 0.5)',
      onClick: () => console.log('Follow Up')
    },
    {
      id: 'templates',
      icon: 'FileText',
      label: 'القوالب',
      color: '#8B5CF6',
      bgGradient: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(167, 139, 250, 0.25) 100%)',
      borderColor: 'rgba(139, 92, 246, 0.5)',
      onClick: () => console.log('Templates')
    }
  ];

  return (
    <Layout
      userRole="client_manager"
      userName="سارة أحمد المدير"
      onQuickMenuToggle={() => setIsQuickMenuOpen(!isQuickMenuOpen)}
    >
      <SubHeader pageTitle="مركز التواصل" />
      <div className="space-y-4 md:space-y-6">
        {/* Communication Action Boxes - Moved to top */}
        <ActionBoxes 
          actions={communicationActions}
          showNewContractModal={false}
          setShowNewContractModal={() => {}}
          contractTypeConfig={[]}
        />
        
        <NotificationBar />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6" style={{ height: 'calc(100vh - 320px)' }}>
          <div className="lg:col-span-3">
            <ChannelSidebar
              activeChannel={activeChannel}
              onChannelChange={setActiveChannel}
            />
          </div>

          <div className="lg:col-span-6">
            <ConversationPanel
              activeChannel={activeChannel}
              selectedConversation={selectedConversation}
              onConversationSelect={setSelectedConversation}
            />
          </div>

          <div className="lg:col-span-3">
            <ContactDetailsPanel selectedConversation={selectedConversation} />
          </div>
        </div>
      </div>

      {/* Action Modals */}
      <WhatsAppActionsModal 
        isOpen={showWhatsAppModal} 
        onClose={() => setShowWhatsAppModal(false)} 
      />
      <SMSActionsModal 
        isOpen={showSMSModal} 
        onClose={() => setShowSMSModal(false)} 
      />
      <EmailActionsModal 
        isOpen={showEmailModal} 
        onClose={() => setShowEmailModal(false)} 
      />
      
      {/* Existing Modals */}
      <NewMessageModal 
        isOpen={showNewMessageModal} 
        onClose={() => setShowNewMessageModal(false)} 
      />
      <BulkSendModal 
        isOpen={showBulkSendModal} 
        onClose={() => setShowBulkSendModal(false)} 
      />
      <ScheduleFollowUpModal 
        isOpen={showScheduleModal} 
        onClose={() => setShowScheduleModal(false)} 
      />
      <TemplatesModal 
        isOpen={showTemplatesModal} 
        onClose={() => setShowTemplatesModal(false)} 
      />
      <CommunicationReportModal 
        isOpen={showReportModal} 
        onClose={() => setShowReportModal(false)} 
      />
    </Layout>
  );
};

export default CommunicationAndFollowUpCenter;